import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, CardMedia, Button, Container, Grid, Chip } from '@mui/material';
import { styled } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Dashboard from './Dashboard';

const RetroContainer = styled(Container)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3f51b5 100%)',
  minHeight: '100vh',
  padding: '2rem 0',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 50%, rgba(0, 191, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(80, 227, 194, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(255, 107, 53, 0.1) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
  },
}));

const RetroTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '2rem',
  color: '#FFFFFF',
  textAlign: 'center',
  marginBottom: '2rem',
  textShadow: '0 0 10px rgba(0, 191, 255, 0.5)',
  '@media (max-width: 600px)': {
    fontSize: '1.2rem',
  },
}));

const ProductCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(145deg, rgba(0, 191, 255, 0.1) 0%, rgba(0, 191, 255, 0.05) 100%)',
  border: '2px solid rgba(0, 191, 255, 0.3)',
  borderRadius: '15px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(0, 191, 255, 0.2)',
    border: '2px solid rgba(0, 191, 255, 0.5)',
  },
}));

const ProductButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
  border: 0,
  borderRadius: '8px',
  color: 'white',
  fontFamily: "'Press Start 2P', cursive",
  fontSize: '0.8rem',
  padding: '12px 24px',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(45deg, #E55A2B 30%, #E8851C 90%)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const ShopPage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [showDashboard, setShowDashboard] = useState(false);

  useEffect(() => {
    // Redirect to home if not authenticated
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleProductClick = () => {
    setShowDashboard(true);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  // Don't render anything if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const products = [
    {
      id: 1,
      name: 'Dog Cards Pack',
      description: 'Get all your saved dog cards printed as physical trading cards',
      price: '$50',
      image: '/api/placeholder/300/200',
      features: ['Up to 25 cards', 'High-quality cardstock', 'Professional printing', 'US shipping included'],
      popular: false,
    },
    {
      id: 2,
      name: 'Custom Dog T-Shirts',
      description: 'Custom t-shirts featuring your favorite dog breeds with personalization',
      price: '$75',
      image: '/api/placeholder/300/200',
      features: ['Multiple colors & sizes', 'Custom dog names', 'Breed descriptions', 'FREE dog card included'],
      popular: true,
    },
    {
      id: 3,
      name: 'Custom Dog Hats',
      description: 'Stylish baseball caps with your favorite dog breed designs',
      price: '$75',
      image: '/api/placeholder/300/200',
      features: ['Adjustable baseball caps', 'Custom dog names', 'Multiple color options', 'FREE dog card included'],
      popular: false,
    },
  ];

  return (
    <RetroContainer maxWidth={false}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Button
            onClick={handleGoHome}
            startIcon={<HomeIcon />}
            sx={{
              color: '#FFFFFF',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Home
          </Button>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: '#50E3C2', fontSize: '0.9rem', mb: 1 }}>
              Welcome back, {user?.email || 'Valued Customer'}!
            </Typography>
          </Box>
        </Box>

        <RetroTitle>
          🛒 Dog T's, Hats n' Cards Shop
        </RetroTitle>

        <Typography
          sx={{
            color: '#FFFFFF',
            textAlign: 'center',
            fontSize: '1.1rem',
            mb: 4,
            maxWidth: '600px',
            margin: '0 auto 2rem auto',
          }}
        >
          Create custom products featuring your favorite dog breeds. All orders include professional printing and US shipping.
        </Typography>

        {/* Products Grid */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {products.map((product) => (
            <Grid item xs={12} md={4} key={product.id}>
              <ProductCard>
                {product.popular && (
                  <Chip
                    label="Most Popular"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      zIndex: 1,
                      backgroundColor: '#FF6B35',
                      color: '#FFFFFF',
                      fontWeight: 'bold',
                    }}
                  />
                )}
                
                <CardMedia
                  sx={{
                    height: 200,
                    background: 'linear-gradient(145deg, #1a237e, #283593)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ShoppingCartIcon sx={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.3)' }} />
                </CardMedia>
                
                <CardContent sx={{ padding: '24px' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 'bold',
                      mb: 1,
                      fontSize: '1.3rem',
                    }}
                  >
                    {product.name}
                  </Typography>
                  
                  <Typography
                    sx={{
                      color: '#50E3C2',
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      mb: 2,
                      fontFamily: "'Press Start 2P', cursive",
                    }}
                  >
                    {product.price}
                  </Typography>
                  
                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    {product.description}
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    {product.features.map((feature, index) => (
                      <Typography
                        key={index}
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '0.8rem',
                          mb: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ marginRight: '8px', color: '#50E3C2' }}>•</span>
                        {feature}
                      </Typography>
                    ))}
                  </Box>
                  
                  <ProductButton
                    fullWidth
                    onClick={handleProductClick}
                    startIcon={<LocalShippingIcon />}
                  >
                    Customize & Order
                  </ProductButton>
                </CardContent>
              </ProductCard>
            </Grid>
          ))}
        </Grid>

        {/* Additional Info */}
        <Box
          sx={{
            backgroundColor: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center',
            border: '1px solid rgba(0, 191, 255, 0.3)',
          }}
        >
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            🚚 Processing & Shipping Information
          </Typography>
          
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              mb: 2,
            }}
          >
            • Processing Time: 2-4 weeks for custom printing<br/>
            • Shipping: Free US shipping included<br/>
            • Quality: Professional printing on premium materials<br/>
            • Tracking: Email notifications with tracking information
          </Typography>
          
          <Typography
            sx={{
              color: '#50E3C2',
              fontSize: '0.9rem',
              fontStyle: 'italic',
            }}
          >
            All products are custom made-to-order for the highest quality results
          </Typography>
        </Box>
      </Container>

      {/* Dashboard Modal */}
      {showDashboard && (
        <Dashboard onClose={() => setShowDashboard(false)} />
      )}
    </RetroContainer>
  );
};

export default ShopPage;
